import { smallCurrencyFormat, twoDecimalPercentFormat } from 'common/formats/formats';
import { GridDateEditor, GridDateValueViewer } from 'components/FeaturedSpreadsheet/components';
import { GPC_HEADER_ROW, TRANSACTION_HEADER_ROW } from 'pages/CompGroups/constants/header';
import { shortDate } from 'utilities';
import { GPCCompExtended, PTCompExtended } from './components/CompGroups/CompGroups.props';
import DeleteableRowHeader from './components/DeleteableRowHeader';
import GridDate from './components/GridDate';
import {
  ENTERPRISE_VALUE_ALIAS,
  LTM_EBITDA_ALIAS,
  LTM_EBITDA_MARGIN_ALIAS,
  LTM_GROSS_MARGIN_ALIAS,
  LTM_REVENUE_ALIAS,
  LTM_REVENUE_GROWTH_ALIAS,
  TRANSACTION_DATE_ALIAS,
} from './constants/constants';

type RemoveItem = (item: GPCCompExtended | PTCompExtended) => void;

export const GPCCompGroups = (companies: GPCCompExtended[], removeItem: RemoveItem, isPreviousVersion: boolean) => {
  const header = GPC_HEADER_ROW;

  const data = companies.map(item => [
    {
      readOnly: true,
      value: (
        <DeleteableRowHeader
          value={item.company_name as string}
          updateObject={item}
          removeItem={removeItem}
          isPreviousVersion={isPreviousVersion}
        />
      ),
    },
    {
      readOnly: true,
      value: item.ticker_symbol,
    },
    {
      readOnly: true,
      value: item.cap_iq_id,
    },
  ]);

  return [header, ...data];
};

const decideFallBack = (prop: string, row: GPCCompExtended | PTCompExtended, comesFromCapIQ: boolean) =>
  comesFromCapIQ ? row[prop as keyof typeof row] : (row[prop as keyof typeof row] ?? 0);

export const transactionCompGroups = (rows: PTCompExtended[], removeItem: RemoveItem, isPreviousVersion: boolean) => {
  const header = TRANSACTION_HEADER_ROW;

  const data = rows.map((row, index) => {
    const rowIndex = index + 1;
    const comesFromCapIQ = row.comes_from_capital_iq ?? false;
    const readOnly = comesFromCapIQ || isPreviousVersion;
    const displayNAforNull = comesFromCapIQ;
    return [
      {
        key: `target_name_${rowIndex}`,
        alias: 'target_name',
        rowIndex,
        valueViewer: (props: any) => (
          <DeleteableRowHeader
            value={row.target_name}
            updateObject={row}
            removeItem={removeItem}
            isPreviousVersion={isPreviousVersion}
            {...props}
          />
        ),
        value: row.target_name,
        gridType: 'string',
        isRequired: true,
        readOnly,
        displayNAforNull,
      },
      {
        key: `acquirer_name_${rowIndex}`,
        value: row.acquirer_name,
        placeholder: 'ENTER DATA',
        alias: 'acquirer_name',
        rowIndex,
        gridType: 'string',
        isRequired: true,
        readOnly,
        displayNAforNull,
      },
      {
        key: `${TRANSACTION_DATE_ALIAS}_${rowIndex}`,
        value: shortDate(row.transaction_date ?? row.transaction_agreement_date),
        alias: TRANSACTION_DATE_ALIAS,
        gridDateComponent: GridDate,
        dataEditor: (props: any) => <GridDateEditor {...props} />,
        valueViewer: (props: any) => <GridDateValueViewer {...props} />,
        rowIndex,
        gridType: 'date',
        isRequired: true,
        readOnly,
      },
      {
        key: `${ENTERPRISE_VALUE_ALIAS}_${rowIndex}`,
        value: decideFallBack(ENTERPRISE_VALUE_ALIAS, row, comesFromCapIQ),
        alias: ENTERPRISE_VALUE_ALIAS,
        gridType: 'number',
        format: smallCurrencyFormat,
        rowIndex,
        isRequired: true,
        readOnly,
        displayNAforNull,
      },
      {
        key: `${LTM_REVENUE_ALIAS}_${rowIndex}`,
        value: decideFallBack(LTM_REVENUE_ALIAS, row, comesFromCapIQ),
        alias: LTM_REVENUE_ALIAS,
        gridType: 'number',
        format: smallCurrencyFormat,
        rowIndex,
        isRequired: true,
        readOnly,
        displayNAforNull,
      },
      {
        key: `${LTM_REVENUE_GROWTH_ALIAS}_${rowIndex}`,
        value: decideFallBack(LTM_REVENUE_GROWTH_ALIAS, row, comesFromCapIQ),
        alias: LTM_REVENUE_GROWTH_ALIAS,
        gridType: 'percentage',
        format: twoDecimalPercentFormat({ minimumFractionDigits: 0, isDecimal: true }),
        rowIndex,
        isRequired: false,
        readOnly,
        displayNAforNull,
      },
      {
        key: `${LTM_EBITDA_ALIAS}_${rowIndex}`,
        value: decideFallBack(LTM_EBITDA_ALIAS, row, comesFromCapIQ),
        alias: LTM_EBITDA_ALIAS,
        gridType: 'number',
        format: smallCurrencyFormat,
        rowIndex,
        isRequired: true,
        readOnly,
        displayNAforNull,
      },
      {
        key: `${LTM_EBITDA_MARGIN_ALIAS}_${rowIndex}`,
        value: decideFallBack(LTM_EBITDA_MARGIN_ALIAS, row, comesFromCapIQ),
        alias: LTM_EBITDA_MARGIN_ALIAS,
        gridType: 'percentage',
        format: twoDecimalPercentFormat({ minimumFractionDigits: 0, isDecimal: true }),
        rowIndex,
        isRequired: false,
        readOnly,
        displayNAforNull,
      },
      {
        key: `${LTM_GROSS_MARGIN_ALIAS}_${rowIndex}`,
        value: decideFallBack(LTM_GROSS_MARGIN_ALIAS, row, comesFromCapIQ),
        alias: LTM_GROSS_MARGIN_ALIAS,
        gridType: 'percentage',
        format: twoDecimalPercentFormat({ minimumFractionDigits: 0, isDecimal: true }),
        rowIndex,
        isRequired: false,
        readOnly,
        displayNAforNull,
      },
    ];
  });

  return [header, ...data];
};
